import { memoryStore, useStore } from "../../../lib/store";

// Legal views carry their active tab (Juisci doc vs partner T&Cs/opt-ins) in
// the view payload — the sheet has no URL to hold it, and business components
// cannot own useState.
export type LegalDocTab = "juisci" | "partners";

// In-sheet navigation views. A discriminated union (not a bare string) so the
// sheet's renderer stays exhaustively checked and future views with payloads
// remain representable.
export type SettingsView =
  | { view: "root" }
  | { view: "account" }
  | { view: "deleteConfirm" }
  | { view: "mainSpecialty" }
  | { view: "language" }
  | { view: "notifications" }
  | { view: "interests" }
  // The Practice Profiles a practitioner holds (practice-profiles ticket 08) —
  // separate from "interests", which is JuisciTopics for the personalised feed.
  | { view: "practice" }
  | { view: "editProfile" }
  | { view: "editAvatar" }
  | { view: "certification" }
  | { view: "rooms" }
  | { view: "partnerSharing" }
  // Pushed from "Partner sharing": the partners list, and the privacy policy
  // of Juisci without the Partners tab of the "privacy" view.
  | { view: "partners" }
  | { view: "juisciPrivacy" }
  // The room's identity travels in the payload rather than being re-read from
  // the rooms list: leaving revalidates that list, and the confirm view is
  // still mounted (sliding out under AnimatePresence) when the room disappears
  // from it.
  | { view: "leaveRoom"; roomId: string; roomName: string }
  | { view: "terms"; tab: LegalDocTab }
  | { view: "privacy"; tab: LegalDocTab };

// `direction` records the last navigation action so the sheet can slide the
// entering view in from the correct side (push → from right, pop → from left).
type SettingsSheetState = {
  open: boolean;
  stack: readonly SettingsView[];
  direction: "push" | "pop";
};

const ROOT_STATE: SettingsSheetState = {
  open: false,
  stack: [{ view: "root" }],
  direction: "push",
};

// Module-level singleton: the sheet is mounted once, app-wide, and driven
// imperatively from anywhere (the profile gear) — mirroring how `AppToaster` /
// `displayToast` expose a route-independent surface.
const store = memoryStore<SettingsSheetState>(ROOT_STATE);

export function useSettingsSheet(): SettingsSheetState {
  const [state] = useStore(store);
  return state;
}

export function openSettings(): void {
  openSettingsAt({ view: "root" });
}

export function closeSettings(): void {
  store.set((prev) => ({ ...prev, open: false }));
}

// Deep-link entry: open the sheet directly on `view` with the root beneath it,
// so the back chevron lands on the settings root and popping never empties the
// stack. Used by out-of-sheet surfaces (the profile edit page's main-specialty
// field) and by the /settings routes, which are how an email reaches a panel.
//
// The root is the stack's floor, so opening AT the root is opening at depth
// one, not pushing the root onto itself — a second copy would make the first
// back tap a no-op.
export function openSettingsAt(view: SettingsView): void {
  store.set({
    open: true,
    stack: view.view === "root" ? [{ view: "root" }] : [{ view: "root" }, view],
    direction: "push",
  });
}

export function pushSettingsView(view: SettingsView): void {
  store.set((prev) => ({
    open: true,
    stack: [...prev.stack, view],
    direction: "push",
  }));
}

// Swap the top view in place (e.g. tab change inside a view) — no slide
// animation, and popping afterwards still returns to the previous view.
export function replaceSettingsView(view: SettingsView): void {
  store.set((prev) => ({
    ...prev,
    stack: [...prev.stack.slice(0, -1), view],
  }));
}

// Unwind the whole stack back to the settings root in a single slide. For flows
// whose own starting view stops making sense once they succeed — leaving your
// last room empties the rooms list the confirm view was reached from.
export function popSettingsToRoot(): void {
  store.set((prev) => ({
    ...prev,
    stack: [{ view: "root" }],
    direction: "pop",
  }));
}

export function popSettingsView(): void {
  store.set((prev) => {
    // Popping the root closes the sheet; otherwise drop the top view.
    if (prev.stack.length <= 1) {
      return { ...prev, open: false, direction: "pop" };
    }
    return {
      ...prev,
      stack: prev.stack.slice(0, -1),
      direction: "pop",
    };
  });
}
